"use client";

import { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import { trackCompareEvent } from "@/lib/compare/analytics";
import {
  BUSINESS_SIZE,
  BUSINESS_SIZE_LABEL,
  type BusinessSize,
} from "@/lib/compare/schema";

/**
 * Phase 50 (HUB-02) — compare hub filter bar.
 *
 * Page-kind filter + business-size relevance control that:
 *   1. Reads initial state from URL query params (?kind=vs&size=2-5)
 *   2. Writes state changes back via window.history.replaceState
 *   3. Toggles data-filter-kind / data-filter-size attrs on the grid container
 *   4. Reorders sections and cards so size matches come first
 *   5. Fires compare_hub_filter_applied on every change
 *
 * **Critical:** page-kind filtering stays CSS-based and size ranking moves the
 * existing server-rendered DOM nodes. Nothing is conditionally rendered, so
 * every card remains in server HTML for AI crawlers (AEO-10), while visual,
 * keyboard and screen-reader order stay aligned after a size is selected.
 *
 * Must be wrapped in <Suspense> by the consuming page.tsx — Next.js 16
 * requires a Suspense boundary around any useSearchParams consumer on an
 * SSG page.
 */

type KindFilter = "all" | "vs" | "roundup" | "alternatives";
type SizeFilter = "all" | BusinessSize;

const KIND_OPTIONS: readonly { value: KindFilter; label: string }[] = [
  { value: "all", label: "All pages" },
  { value: "vs", label: "Head-to-head" },
  { value: "roundup", label: "Roundups" },
  { value: "alternatives", label: "Alternatives" },
];

function isKindFilter(v: string | null): v is KindFilter {
  return (
    v === "all" || v === "vs" || v === "roundup" || v === "alternatives"
  );
}

function isSizeFilter(v: string | null): v is SizeFilter {
  if (v === "all") return true;
  if (v === null) return false;
  return (BUSINESS_SIZE as readonly string[]).includes(v);
}

function getDefaultOrder(element: HTMLElement): number {
  const order = Number.parseInt(element.dataset.defaultOrder ?? "", 10);
  return Number.isNaN(order) ? Number.MAX_SAFE_INTEGER : order;
}

function includesToken(element: HTMLElement, attribute: string, token: string) {
  return (element.getAttribute(attribute) ?? "")
    .split(/\s+/)
    .includes(token);
}

function rankBySize(
  elements: HTMLElement[],
  size: SizeFilter,
  attribute: string,
): HTMLElement[] {
  return [...elements].sort((a, b) => {
    if (size !== "all") {
      const aMatches = includesToken(a, attribute, size);
      const bMatches = includesToken(b, attribute, size);
      if (aMatches !== bMatches) return aMatches ? -1 : 1;
    }
    return getDefaultOrder(a) - getDefaultOrder(b);
  });
}

function rankGridBySize(grid: HTMLElement, size: SizeFilter) {
  const sections = Array.from(
    grid.querySelectorAll<HTMLElement>(":scope > [data-kind-section]"),
  );
  grid.append(
    ...rankBySize(sections, size, "data-priority-business-sizes"),
  );

  for (const cardGrid of grid.querySelectorAll<HTMLElement>(
    '[data-compare-card-grid="true"]',
  )) {
    const cards = Array.from(
      cardGrid.querySelectorAll<HTMLElement>(
        ':scope > [data-page-card="true"]',
      ),
    );
    cardGrid.append(...rankBySize(cards, size, "data-business-sizes"));
  }
}

export function CompareHubFilterBar() {
  const searchParams = useSearchParams();

  const [kind, setKind] = useState<KindFilter>(() => {
    const v = searchParams.get("kind");
    return isKindFilter(v) ? v : "all";
  });
  const [size, setSize] = useState<SizeFilter>(() => {
    const v = searchParams.get("size");
    return isSizeFilter(v) ? v : "all";
  });

  // Sync URL + grid data attrs whenever state changes.
  useEffect(() => {
    if (typeof window === "undefined") return;

    const url = new URL(window.location.href);
    if (kind === "all") url.searchParams.delete("kind");
    else url.searchParams.set("kind", kind);
    if (size === "all") url.searchParams.delete("size");
    else url.searchParams.set("size", size);
    window.history.replaceState({}, "", url.toString());

    const grid = document.querySelector<HTMLElement>(
      '[data-compare-hub-grid="true"]',
    );
    if (grid) {
      grid.setAttribute("data-filter-kind", kind);
      grid.setAttribute("data-filter-size", size);
      rankGridBySize(grid, size);
    }

    trackCompareEvent({
      event: "compare_hub_filter_applied",
      filter_kind: kind,
      filter_size: size,
    });
  }, [kind, size]);

  // React to back/forward navigation (browser history restores different
  // URL params — sync our local state to match).
  useEffect(() => {
    const k = searchParams.get("kind");
    const s = searchParams.get("size");
    if (isKindFilter(k) && k !== kind) setKind(k);
    if (isSizeFilter(s) && s !== size) setSize(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const chipBase =
    "inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold transition-colors";
  const chipInactive =
    "border-accent/30 bg-accent-light text-brand-700 hover:bg-accent-light";
  const chipActive = "border-accent/30 bg-accent-light text-brand-700";

  return (
    <div
      className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center"
      role="group"
      aria-label="Compare filter"
      data-testid="compare-hub-filter-bar"
    >
      <div className="flex flex-wrap gap-2">
        <span className="self-center text-xs font-semibold uppercase tracking-wide text-gray-500">
          Type:
        </span>
        {KIND_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setKind(opt.value)}
            aria-pressed={kind === opt.value}
            className={`${chipBase} ${kind === opt.value ? chipActive : chipInactive}`}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <span className="self-center text-xs font-semibold uppercase tracking-wide text-gray-500">
          Size:
        </span>
        <button
          type="button"
          onClick={() => setSize("all")}
          aria-pressed={size === "all"}
          className={`${chipBase} ${size === "all" ? chipActive : chipInactive}`}
        >
          All sizes
        </button>
        {BUSINESS_SIZE.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setSize(s)}
            aria-pressed={size === s}
            className={`${chipBase} ${size === s ? chipActive : chipInactive}`}
          >
            {BUSINESS_SIZE_LABEL[s]}
          </button>
        ))}
      </div>
    </div>
  );
}
